import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getProfile, adminHideProfile, adminUnhideProfile } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { MapPin, Globe, ShieldAlert, ShieldCheck, ExternalLink, RefreshCw, ArrowLeft, Music, Play, Send, FileText } from 'lucide-react';
import { getExperienceLevelLabel, getRateTypeLabel, getThomannSearchUrl } from '../lib/utils';
import { toast } from 'sonner';
import { RiderUpload } from '../components/RiderUpload';
import { PageSkeleton, Bone, BoneCard } from '../components/PageSkeleton';

// ── Media helpers (showcase) — pure, client-side only, no network ─────────────
// Parse a YouTube/Vimeo URL into { service, id }. Returns null if unrecognized.
function parseVideoUrl(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '');
    if (host === 'youtu.be') {
      const id = u.pathname.slice(1).split('/')[0];
      return id ? { service: 'youtube', id, url } : null;
    }
    if (host === 'youtube.com' || host === 'm.youtube.com') {
      const v = u.searchParams.get('v');
      if (v) return { service: 'youtube', id: v, url };
      const m = u.pathname.match(/\/(?:embed|shorts)\/([^/?#]+)/);
      return m ? { service: 'youtube', id: m[1], url } : null;
    }
    if (host === 'vimeo.com' || host === 'player.vimeo.com') {
      const m = u.pathname.match(/(\d{6,})/);
      return m ? { service: 'vimeo', id: m[1], url } : null;
    }
  } catch (e) { /* invalid URL */ }
  return null;
}

// Detect a streaming service from the host. Falls back to a generic "Portfolio"
// chip so a non-streaming link is never mislabeled. NOTE: reuses the grab-bag
// portfolioUrl field; a dedicated streaming_url is the honest fix (see handoff).
function detectStreaming(url) {
  if (!url) return null;
  try {
    const host = new URL(url).hostname.replace(/^www\./, '');
    if (host === 'open.spotify.com') return { name: 'Spotify', url };
    if (host === 'music.apple.com') return { name: 'Apple Music', url };
    if (host.endsWith('bandcamp.com')) return { name: 'Bandcamp', url };
    if (host === 'soundcloud.com') return { name: 'SoundCloud', url };
    return { name: 'Portfolio', url, generic: true };
  } catch (e) {
    return null;
  }
}

// Link-confirm before leaving the app — surface the destination host first.
function openExternalWithConfirm(url) {
  try {
    const host = new URL(url).hostname.replace(/^www\./, '');
    // eslint-disable-next-line no-alert
    if (window.confirm(`Open ${host} in a new tab?`)) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  } catch (e) { /* invalid URL — do nothing */ }
}

export default function MusicianDetailPage({ profileId: propId, embedded = false }) {
  const params = useParams();
  const id = propId || params.id;
  const navigate = useNavigate();
  const { user } = useAuth();
  const [musician, setMusician] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!embedded && id === 'me') { navigate('/musicians/me', { replace: true }); return; }
    loadMusician();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate, embedded]);

  const loadMusician = async () => {
    try {
      const response = await getProfile(id);
      if (response.data.type !== 'MUSICIAN') {
        if (!embedded) navigate('/musicians', { replace: true });
        return;
      }
      setMusician(response.data);
    } catch (error) {
      if (!embedded) {
        toast.error('Musician not found');
        navigate('/musicians', { replace: true });
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={embedded ? 'px-5 py-4' : 'page-container-md'}>
        <PageSkeleton label="Loading musician">
          {!embedded && <Bone w={64} h={14} className="mb-5" />}
          <BoneCard className="p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <Bone w="68%" h={34} />
                <Bone w={64} h={14} className="mt-3" />
                <Bone w="58%" h={14} className="mt-4" />
              </div>
              <Bone w={64} h={24} tone="amber" />
            </div>
          </BoneCard>
          <div className="flex justify-center my-5"><Bone w={150} h={48} r="pill" tone="amber" /></div>
          <BoneCard className="p-5">
            <Bone w="46%" h={20} />
            <div className="grid grid-cols-2 gap-2 mt-4"><Bone h={68} r="md" /><Bone h={68} r="md" /></div>
            <Bone w={80} h={12} className="mt-5" />
            <div className="flex gap-2 mt-2"><Bone w={90} h={24} /></div>
            <Bone w={110} h={12} className="mt-4" />
            <div className="flex gap-2 mt-2"><Bone w={48} h={24} /><Bone w={40} h={24} /><Bone w={48} h={24} /></div>
          </BoneCard>
        </PageSkeleton>
      </div>
    );
  }

  if (!musician) return null;

  const rate = musician.baseRate;
  const rateLabel = getRateTypeLabel(musician.rateType);

  // Experience ripening: show the witnessed gig count once it exists, otherwise
  // fall back to the muted declared experience level. Both fields already exist.
  const gigCount = musician.stage8GigCount || 0;
  const declaredExperience = musician.musicianExperienceLevel || musician.experienceLevel;

  // Doublings = additional instruments, minus the primary (compared loosely so a
  // primary echoed in instruments[] isn't shown twice). Plain declared chips.
  const primaryKey = (musician.primaryInstrument || '').trim().toLowerCase();
  const doublings = (musician.instruments || []).filter(
    (inst) => inst && inst.trim().toLowerCase() !== primaryKey
  );

  // Media (showcase): streaming chip from portfolioUrl; video tiles from videos[].
  // videos[] is NOT a backend field yet — the editor writes it but the API drops it,
  // so videoTiles is empty (grid hidden) until the field lands. See handoff.
  const streaming = detectStreaming(musician.portfolioUrl);
  const videoTiles = (musician.videos || []).map(parseVideoUrl).filter(Boolean);

  return (
    <div className={embedded ? 'px-5 py-4' : 'page-container-md'} data-testid="musician-detail-page">

      {/* Back — hidden when embedded in 3-pane directory */}
      {!embedded && (
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 mb-5 transition-colors"
          style={{ color: 'var(--s8-text-muted)', fontFamily: 'var(--s8-font-mono)', fontSize: '12px', letterSpacing: '1px' }}
          data-testid="back-btn"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>BACK</span>
        </button>
      )}

      {/* ── Header: Name + Instrument + Rate + Location ── */}
      <div
        className="mb-5 p-4 s8-glow-border"
        style={{
          background: 'linear-gradient(180deg, rgba(240, 235, 224, 0.02) 0%, var(--s8-bg-surface) 100%)',
          border: '1px solid var(--s8-border-card)',
          borderRadius: 'var(--s8-radius-lg)',
          boxShadow: 'var(--card-shadow)',
        }}
        data-testid="musician-header"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1
              style={{ fontFamily: 'var(--s8-font-display)', fontWeight: 600, fontSynthesis: 'none', fontSize: 'clamp(1.875rem, 1.875rem + 1vw, 2.25rem)', lineHeight: 1.1, letterSpacing: '-0.02em', textWrap: 'balance', color: 'var(--s8-text-main)' }}
              data-testid="musician-name"
            >
              {musician.displayName}
            </h1>
            {musician.primaryInstrument && (
              <div className="mt-1.5" style={{ fontFamily: 'var(--s8-font-body)', fontWeight: 600, fontSize: '14px', color: 'var(--s8-text-body)' }} data-testid="musician-instrument-header">
                {musician.primaryInstrument}
              </div>
            )}
          </div>
          {rate && (
            <div className="shrink-0 text-right">
              <span style={{ fontFamily: 'var(--s8-font-body)', fontWeight: 600, fontSize: '22px', color: 'var(--s8-amber)' }} data-testid="musician-rate">
                €{rate}
              </span>
              <div style={{ fontFamily: 'var(--s8-font-body)', fontSize: '12px', color: 'var(--s8-text-muted)' }}>{rateLabel}</div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5 mt-2" style={{ fontFamily: 'var(--s8-font-body)', fontSize: '14px', color: 'var(--s8-text-muted)' }} data-testid="musician-location">
          <MapPin className="h-3.5 w-3.5 shrink-0" />
          <span>
            {musician.city}{musician.country ? `, ${musician.country}` : ''}
            {musician.travelRadiusKm ? ` · ${musician.travelRadiusKm}km radius` : ''}
          </span>
        </div>
      </div>

      {/* ── Primary Action ── */}
      <div className="flex justify-center mb-6">
        <button
          className="btn-primary px-8 py-3 text-sm tracking-wider sm:min-w-[280px] sm:px-12 sm:py-3.5"
          data-testid="invite-to-gig-btn"
          onClick={() => toast.info('Invite flow coming soon')}
        >
          <Send className="h-4 w-4" />
          INVITE TO GIG
        </button>
      </div>

      {/* ── Capabilities Card: Instrument, Experience, Genres ── */}
      <div
        className="mb-4 p-5"
        style={{
          background: 'linear-gradient(180deg, rgba(240, 235, 224, 0.02) 0%, var(--s8-bg-surface) 100%)',
          border: '1px solid var(--s8-border-card)',
          borderRadius: 'var(--s8-radius-lg)',
          boxShadow: 'var(--card-shadow)',
        }}
        data-testid="capabilities-card"
      >
        <h2
          className="mb-3"
          style={{ fontFamily: 'var(--s8-font-mono)', fontWeight: 500, fontSize: '18px', color: 'var(--s8-text-main)', letterSpacing: '1.5px', lineHeight: 1.15 }}
        >
          CAPABILITIES
        </h2>
        <div className="grid grid-cols-2 gap-px mb-3" style={{ background: 'var(--border-subtle)', borderRadius: 'var(--s8-radius-md)', overflow: 'hidden' }}>
          <div className="p-3" style={{ background: 'var(--s8-bg-elevated)' }}>
            <div style={{ fontFamily: 'var(--s8-font-mono)', fontSize: '11px', color: 'var(--s8-text-muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
              Instrument
            </div>
            <div style={{ fontFamily: 'var(--s8-font-body)', fontWeight: 600, fontSize: '16px', color: 'var(--s8-text-main)' }}>
              {musician.primaryInstrument || '—'}
            </div>
          </div>
          <div className="p-3" style={{ background: 'var(--s8-bg-elevated)' }}>
            <div style={{ fontFamily: 'var(--s8-font-mono)', fontSize: '11px', color: 'var(--s8-text-muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
              Experience
            </div>
            {gigCount > 0 ? (
              <div style={{ fontFamily: 'var(--s8-font-body)', fontWeight: 600, fontSize: '16px', color: 'var(--s8-text-main)' }}>
                {gigCount} verified {gigCount === 1 ? 'gig' : 'gigs'}
              </div>
            ) : (
              <div style={{ fontFamily: 'var(--s8-font-body)', fontWeight: 600, fontSize: '16px', color: 'var(--s8-text-muted)' }}>
                {declaredExperience ? getExperienceLevelLabel(declaredExperience) : '—'}
              </div>
            )}
          </div>
        </div>
        {doublings.length > 0 && (
          <div className="mb-3">
            <div style={{ fontFamily: 'var(--s8-font-mono)', fontSize: '11px', color: 'var(--s8-text-muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>
              Doublings
            </div>
            <div className="flex flex-wrap gap-1.5">
              {doublings.map((inst) => (
                <span key={inst} className="browse-tag">{inst}</span>
              ))}
            </div>
          </div>
        )}
        {musician.genres?.length > 0 && (
          <div>
            <div style={{ fontFamily: 'var(--s8-font-mono)', fontSize: '11px', color: 'var(--s8-text-muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>
              Primary Genres
            </div>
            <div className="flex flex-wrap gap-1.5">
              {musician.genres.map((g, i) => (
                <span key={g} className="browse-tag">{g}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Operational Record: STAGE8 Gigs, On Time, Would Re-hire ── */}
      <div
        className="mb-4 p-5 s8-glow-border"
        style={{
          background: 'linear-gradient(180deg, rgba(240, 235, 224, 0.02) 0%, var(--s8-bg-surface) 100%)',
          border: '1px solid var(--s8-border-card)',
          borderRadius: 'var(--s8-radius-lg)',
          boxShadow: 'var(--card-shadow)',
        }}
        data-testid="operational-record-card"
      >
        <div style={{ position: 'relative', zIndex: 2 }}>
          <h2
            className="mb-3"
            style={{ fontFamily: 'var(--s8-font-mono)', fontWeight: 500, fontSize: '18px', color: 'var(--s8-text-main)', letterSpacing: '1.5px', lineHeight: 1.15 }}
          >
            OPERATIONAL RECORD
          </h2>
        <div className="grid grid-cols-3 gap-px" style={{ background: 'var(--border-subtle)', borderRadius: 'var(--s8-radius-md)', overflow: 'hidden' }}>
          {/* STAGE8 Gigs — the anchor metric; the true count, and 0 is an honest fact */}
          <div className="p-3" style={{ background: 'var(--s8-bg-elevated)' }}>
            <div style={{ fontFamily: 'var(--s8-font-mono)', fontSize: '11px', color: 'var(--s8-text-muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
              STAGE8 Gigs
            </div>
            <div style={{ fontFamily: 'var(--s8-font-body)', fontWeight: 600, fontSize: '18px', color: 'var(--s8-text-main)' }} data-testid="musician-gig-count">
              {gigCount}
            </div>
          </div>
          {/* On Time — no aggregated profile field exists yet; honest empty until it does */}
          <div className="p-3" style={{ background: 'var(--s8-bg-elevated)' }}>
            <div style={{ fontFamily: 'var(--s8-font-mono)', fontSize: '11px', color: 'var(--s8-text-muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
              On Time
            </div>
            <div style={{ fontFamily: 'var(--s8-font-body)', fontWeight: 400, fontSize: '13px', color: 'var(--s8-text-muted)' }} data-testid="musician-on-time">
              no record yet
            </div>
          </div>
          {/* Would Re-hire — reads rebookRate for now; muted until real data backs it (never —, never 0%) */}
          <div className="p-3" style={{ background: 'var(--s8-bg-elevated)' }}>
            <div style={{ fontFamily: 'var(--s8-font-mono)', fontSize: '11px', color: 'var(--s8-text-muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
              Would Re-hire
            </div>
            {musician.rebookRate != null && gigCount > 0 ? (
              <div style={{ fontFamily: 'var(--s8-font-body)', fontWeight: 600, fontSize: '18px', color: 'var(--s8-amber)' }} data-testid="musician-rehire">
                {musician.rebookRate}%
              </div>
            ) : (
              <div style={{ fontFamily: 'var(--s8-font-body)', fontWeight: 400, fontSize: '13px', color: 'var(--s8-text-muted)' }} data-testid="musician-rehire">
                no record yet
              </div>
            )}
          </div>
        </div>
        </div>
      </div>

      {/* ── Details Card: Genres removed (in Capabilities), keep Event Types, Languages, Gear ── */}
      {(musician.eventTypes?.length > 0 || musician.languages?.length > 0 || musician.gearList?.length > 0) && (
        <div
          className="mb-4 p-5"
          style={{
            background: 'linear-gradient(180deg, rgba(240, 235, 224, 0.02) 0%, var(--s8-bg-surface) 100%)',
            border: '1px solid var(--s8-border-card)',
            borderRadius: 'var(--s8-radius-lg)',
            boxShadow: 'var(--card-shadow)',
          }}
          data-testid="details-card"
        >
          <h2
            className="mb-4"
            style={{ fontFamily: 'var(--s8-font-mono)', fontWeight: 500, fontSize: '18px', color: 'var(--s8-text-main)', letterSpacing: '1.5px', lineHeight: 1.15 }}
          >
            DETAILS
          </h2>
          <div className="space-y-5">
            {/* Event Types */}
            {musician.eventTypes?.length > 0 && (
              <div>
                <div style={{ fontFamily: 'var(--s8-font-mono)', fontSize: '11px', color: 'var(--s8-text-muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
                  Event Types
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {musician.eventTypes.map((t, i) => (
                    <span key={t} className="browse-tag">{t}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Languages */}
            {musician.languages?.length > 0 && (
              <div>
                <div style={{ fontFamily: 'var(--s8-font-mono)', fontSize: '11px', color: 'var(--s8-text-muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
                  Languages
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {musician.languages.map((l, i) => (
                    <span key={l} className="browse-tag flex items-center gap-1">
                      <Globe className="h-3 w-3" style={{ color: 'var(--s8-text-muted)' }} />
                      {l}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Gear */}
            {musician.gearList?.length > 0 && (
              <div>
                <div style={{ fontFamily: 'var(--s8-font-mono)', fontSize: '11px', color: 'var(--s8-text-muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
                  Gear
                </div>
                <div className="space-y-1.5">
                  {musician.gearList.map((gear, i) => (
                    <a
                      key={gear}
                      href={getThomannSearchUrl(gear)}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="flex items-center gap-1.5 transition-colors hover:text-[var(--s8-amber)]"
                      style={{ fontFamily: 'var(--s8-font-body)', fontSize: '14px', color: 'var(--s8-text-body)' }}
                    >
                      <span>{gear}</span>
                      <ExternalLink className="h-3 w-3" style={{ color: 'var(--s8-text-muted)' }} />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Media (showcase): Videos lead, then Streaming, then EPK stub ── */}
      <div
        className="mb-4 p-5"
        style={{
          background: 'linear-gradient(180deg, rgba(240, 235, 224, 0.02) 0%, var(--s8-bg-surface) 100%)',
          border: '1px solid var(--s8-border-card)',
          borderRadius: 'var(--s8-radius-lg)',
          boxShadow: 'var(--card-shadow)',
        }}
        data-testid="media-card"
      >
        <h2
          className="mb-4"
          style={{ fontFamily: 'var(--s8-font-mono)', fontWeight: 500, fontSize: '18px', color: 'var(--s8-text-main)', letterSpacing: '1.5px', lineHeight: 1.15 }}
        >
          MEDIA
        </h2>
        <div className="space-y-5">

          {/* Videos — lead showcase signal, largest. Hidden until videos[] backend field lands. */}
          {videoTiles.length > 0 && (
            <div data-testid="media-videos">
              <div style={{ fontFamily: 'var(--s8-font-mono)', fontSize: '11px', color: 'var(--s8-text-muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
                Videos
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {videoTiles.map((v) => (
                  <button
                    key={v.url}
                    type="button"
                    onClick={() => openExternalWithConfirm(v.url)}
                    className="group relative block w-full overflow-hidden"
                    style={{ aspectRatio: '16 / 9', borderRadius: 'var(--s8-radius-md)', border: '1px solid var(--s8-border-card)', background: 'var(--s8-bg-elevated)' }}
                    data-testid="media-video-tile"
                  >
                    {v.service === 'youtube' && (
                      <img
                        src={`https://img.youtube.com/vi/${v.id}/hqdefault.jpg`}
                        alt="Video thumbnail"
                        loading="lazy"
                        className="absolute inset-0 h-full w-full object-cover"
                      />
                    )}
                    {v.service === 'vimeo' && (
                      <span className="absolute bottom-1.5 left-2" style={{ fontFamily: 'var(--s8-font-mono)', fontSize: '10px', letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--s8-text-muted)' }}>
                        Vimeo
                      </span>
                    )}
                    <span className="absolute inset-0 flex items-center justify-center">
                      <Play className="h-9 w-9 transition-transform group-hover:scale-110" style={{ color: 'var(--s8-text-main)', fill: 'rgba(14, 17, 16, 0.55)' }} />
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Streaming — service-detected chip from portfolioUrl; generic "Portfolio" fallback */}
          {streaming && (
            <div data-testid="media-streaming">
              <div style={{ fontFamily: 'var(--s8-font-mono)', fontSize: '11px', color: 'var(--s8-text-muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
                Streaming
              </div>
              <button
                type="button"
                onClick={() => openExternalWithConfirm(streaming.url)}
                className="inline-flex items-center gap-2 px-3 py-2 transition-colors hover:border-[var(--s8-amber)]"
                style={{ borderRadius: 'var(--s8-radius-pill)', border: '1px solid var(--s8-border-card)', background: 'var(--s8-bg-elevated)' }}
                data-testid="streaming-chip"
              >
                <Music className="h-3.5 w-3.5" style={{ color: 'var(--s8-amber)' }} />
                <span style={{ fontFamily: 'var(--s8-font-body)', fontWeight: 600, fontSize: '14px', color: 'var(--s8-text-main)' }}>{streaming.name}</span>
                <ExternalLink className="h-3 w-3" style={{ color: 'var(--s8-text-muted)' }} />
              </button>
            </div>
          )}

          {/* EPK — disabled stub, no logic */}
          <div data-testid="media-epk">
            <div style={{ fontFamily: 'var(--s8-font-mono)', fontSize: '11px', color: 'var(--s8-text-muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
              EPK
            </div>
            <div
              className="inline-flex items-center gap-2 px-3 py-2"
              style={{ borderRadius: 'var(--s8-radius-md)', border: '1px dashed var(--s8-border-card)', background: 'var(--s8-bg-elevated)', opacity: 0.55, cursor: 'not-allowed' }}
              aria-disabled="true"
            >
              <FileText className="h-3.5 w-3.5" style={{ color: 'var(--s8-text-muted)' }} />
              <span style={{ fontFamily: 'var(--s8-font-body)', fontSize: '14px', color: 'var(--s8-text-muted)' }}>EPK upload — coming soon</span>
            </div>
          </div>

        </div>
      </div>

      {/* ── About ── */}
      {musician.shortBio && (
        <div
          className="mb-4 p-5"
          style={{
            background: 'linear-gradient(180deg, rgba(240, 235, 224, 0.02) 0%, var(--s8-bg-surface) 100%)',
            border: '1px solid var(--s8-border-card)',
            borderRadius: 'var(--s8-radius-lg)',
            boxShadow: 'var(--card-shadow)',
          }}
          data-testid="about-card"
        >
          <h2
            className="mb-3"
            style={{ fontFamily: 'var(--s8-font-mono)', fontWeight: 500, fontSize: '18px', color: 'var(--s8-text-main)', letterSpacing: '1.5px', lineHeight: 1.15 }}
          >
            ABOUT
          </h2>
          <p style={{ fontFamily: 'var(--s8-font-body)', fontSize: '14px', lineHeight: 1.55, color: 'var(--s8-text-body)' }}>
            {musician.shortBio}
          </p>
        </div>
      )}

      {/* ── Riders ── */}
      <div className="mb-4">
        <div
          className="p-5"
          style={{
            background: 'linear-gradient(180deg, rgba(240, 235, 224, 0.02) 0%, var(--s8-bg-surface) 100%)',
            border: '1px solid var(--s8-border-card)',
            borderRadius: 'var(--s8-radius-lg)',
            boxShadow: 'var(--card-shadow)',
          }}
          data-testid="riders-card"
        >
          <h2
            className="mb-3"
            style={{ fontFamily: 'var(--s8-font-mono)', fontWeight: 500, fontSize: '18px', color: 'var(--s8-text-main)', letterSpacing: '1.5px', lineHeight: 1.15 }}
          >
            RIDERS
          </h2>
          <RiderUpload profileId={id} editable={false} />
        </div>
      </div>

      {/* Admin Controls */}
      {user?.isAdmin && (
        <div className="mt-6 pt-4 flex items-center justify-between" style={{ borderTop: '1px solid var(--border-subtle)' }} data-testid="admin-controls">
          <div className="flex items-center gap-2" style={{ fontFamily: 'var(--s8-font-mono)', fontSize: '11px', color: 'var(--s8-red-tag-text)' }}>
            <ShieldAlert className="h-3 w-3" />
            <span>ADMIN</span>
          </div>
          {musician.hidden ? (
            <Button variant="outline" size="sm" className="border-[var(--s8-amber)]/30 text-[var(--s8-amber)] hover:bg-[var(--s8-amber)]/15 text-xs"
              onClick={async () => { try { await adminUnhideProfile(musician.id); toast.success('Restored'); loadMusician(); } catch { toast.error('Failed'); } }}
              data-testid="admin-unhide-profile-btn">
              <ShieldCheck className="h-3 w-3 mr-1" /> Restore
            </Button>
          ) : (
            <Button variant="outline" size="sm" className="border-red-500/25 text-red-400 hover:bg-red-500/10 text-xs"
              onClick={async () => { try { await adminHideProfile(musician.id); toast.success('Hidden'); navigate('/musicians'); } catch { toast.error('Failed'); } }}
              data-testid="admin-hide-profile-btn">
              <ShieldAlert className="h-3 w-3 mr-1" /> Hide
            </Button>
          )}
        </div>
      )}

    </div>
  );
}
